import { ComputedRef, computed, reactive } from 'vue'
import { authMethods, CreatorType } from '@/constants'
import { StoreResponse, APIResponse, APIResponseCode, IWallPayload } from '@/common/interfaces'
import { Creator, IUser, User } from '@/common/classes'
import useApiService from '@/composables/api'

const apiService = useApiService()

interface State {
  user: User | undefined
  isRegistered: boolean
}

const state: State = reactive({
  user: undefined,
  isRegistered: false,
})

interface Actions {
  reset: () => void
  logout: () => void
  getUserDetails: () => Promise<StoreResponse>
  isValidSession: () => Promise<{ valid: boolean; authenticated: boolean }>
  anonymousLogin(value: IWallPayload): Promise<{ status: boolean; id: string }>
}

interface Getters {
  user: ComputedRef<User | undefined>
  getUserAsCreator: ComputedRef<Creator | undefined>
}

interface ServiceInterface {
  state: State
  actions: Actions
  getters: Getters
}

function useUserStore(): ServiceInterface {
  const actions = {
    reset(): void {
      state.user = undefined
      state.isRegistered = false
    },

    async anonymousLogin(payload: IWallPayload): Promise<{ status: boolean; id: string }> {
      let authType = authMethods.find((m) => m.label === 'anonymous')
      if (authType) {
        const res = await apiService.request.anonymousLogin(payload)
        if (res.status === 200 && res.data && res.data.id) {
          return { status: true, id: res.data.id }
        }
      }
      return { status: false, id: '' }
    },

    async getUserDetails(): Promise<StoreResponse> {
      const res = await apiService.request.fetchUserDetails()
      if ((res.errors && res.errors.length > 0) || !res.data) {
        const error = 'User query contains no records or invalid session'
        console.error(res.errors ? res.errors : error)
        actions.reset() // reset user store to initial state
      } else {
        state.user = new User(res.data)
        return { success: true }
      }
      return { success: false, error: res.errors }
    },

    // 'valid' - a session is current at the server (FEIDE or Anonymous)
    // 'authenticated' - the session was created by FEIDE authentication
    async isValidSession(): Promise<{ valid: boolean; authenticated: boolean }> {
      const res = await apiService.request.sessionIsActive()
      if (res.status === APIResponseCode.Ok) {
        const authenticated = res.data?.authenticated ?? false
        return { valid: true, authenticated }
      } else return { valid: false, authenticated: false }
    },

    logout(): void {
      apiService.request.logout()
    },
  }

  const getters = {
    get user(): ComputedRef<User | undefined> {
      return computed(() => {
        return state.user
      })
    },

    get getUserAsCreator(): ComputedRef<Creator | undefined> {
      return computed(() => {
        if (state.user) {
          const type = state.user.status.isRegistered ? CreatorType.REGISTERED : CreatorType.ANONYMOUS
          return {
            type,
            username: state.user.profile.username || state.user.profile.username,
            user_id: state.user._id,
          }
        }
        return undefined
      })
    },
  }

  return { state, actions, getters }
}

export { useUserStore }
